/**
 * The errands of the glen (RC2-01).
 *
 * The core loop's mini-quests, authored as data beside villager speech
 * (`villagers.ts`) and the valley's memory (`rumours.ts`). An errand is an
 * *invitation* — the Elder speaks of a wolf at the pens, a herder of a strayed
 * ewe — and nothing more: taking one on grants no skill and no reward, because
 * the doing is the only teacher (RC2-02). What it does is point a newcomer at a
 * mechanic and, when the ordinary world event that fulfils it fires, close
 * itself off the stream (`@annwn/sim`'s `closeErrands`). No branching dialogue,
 * no scripted quest code — a giver, authored objectives, declarative offer
 * rules, and facts resolved from the live world when the words are spoken.
 *
 * The opening is shared by every background. Its order is the survival spine a
 * newcomer needs; backgrounds still change the body, kit and familiarities with
 * which they meet it. Later open-world errands use the same rule vocabulary and
 * may branch, overlap, or be available only at a particular time.
 *
 * **Guard the mystery.** No errand sends anybody to the Deer, and none names the
 * otherworld register. Legibility is for verbs, not for wonder.
 */
import { addItem, countOf, createInventory, } from '@annwn/sim';
const after = (id) => ({ kind: 'errand', id, state: 'completed' });
/* -------------------------------------------------------------------------- *
 * The shared opening — one night at Llan-y-Nant
 * -------------------------------------------------------------------------- */
export const ERRAND_FIND_ANWEN = {
    id: 'find-anwen',
    giver: 'elder',
    prompt: 'Anwen keeps the fold and will know what work needs doing.',
    facts: [{ kind: 'villager-location', villager: 'herder-anwen' }],
    trigger: { kind: 'talk-to', villager: 'herder-anwen' },
    offerWhen: { kind: 'always' },
    ring: 0,
};
export const ERRAND_SUPPER = {
    id: 'supper-for-the-watch',
    giver: 'herder-anwen',
    prompt: 'The watch cannot eat from an empty hearth. Gather dry wood and hazelnuts in the woods north-west of the hall.',
    objectives: [
        {
            id: 'firewood',
            text: 'Gather 3 firewood in the woods north-west of the hall.',
            trigger: { kind: 'forage', item: 'firewood' },
            count: 3,
        },
        {
            id: 'hazelnuts',
            text: 'Gather 3 hazelnuts in the woods north-west of the hall.',
            trigger: { kind: 'forage', item: 'hazelnuts' },
            count: 3,
        },
    ],
    offerWhen: after(ERRAND_FIND_ANWEN.id),
    ring: 0,
};
export const ERRAND_WATCH_FIRE = {
    id: 'fire-for-the-watch',
    giver: 'herder-anwen',
    prompt: 'Good. Take it into the herders’ house and lay it on the dry floor by the hearth. Your tinder will take under a roof.',
    dialogue: { active: 'I have brought you the fallen wood.' },
    trigger: { kind: 'fire-lit' },
    offerWhen: after(ERRAND_SUPPER.id),
    ring: 0,
};
export const ERRAND_ROAST_NUTS = {
    id: 'roast-for-the-watch',
    giver: 'herder-anwen',
    prompt: 'Put the hazelnuts to the herders’ fire. They will keep, and so will the watch.',
    trigger: { kind: 'cook', product: 'roasted-hazelnuts' },
    offerWhen: after(ERRAND_WATCH_FIRE.id),
    reward: {
        itemId: 'knife',
        text: 'You take the hearth knife in hand. It will serve for work and close quarters.',
    },
    ring: 0,
};
/* -------------------------------------------------------------------------- *
 * The first ring — a doorway per background
 * -------------------------------------------------------------------------- */
/**
 * *The Meddyg's stores are short — yarrow grows by the ford.* The Llysieuwr's
 * first: forage, and the medicine it feeds. Closed by gathering yarrow; the
 * binding of a villager's cut is the surface it points at next, not its closer.
 */
export const ERRAND_YARROW = {
    id: 'yarrow-for-the-meddyg',
    giver: 'meddyg',
    prompt: 'My shelves do not fill themselves. There is yarrow by the ford — bring me some.',
    objectives: [
        {
            id: 'yarrow',
            text: 'Gather yarrow by the ford.',
            trigger: { kind: 'forage', item: 'yarrow' },
        },
        {
            id: 'sphagnum',
            text: 'Gather sphagnum for a dressing in the bog.',
            trigger: { kind: 'forage', item: 'sphagnum' },
        },
    ],
    offerWhen: after('take-meat-to-the-meddyg'),
    ring: 1,
};
/**
 * *A rabbit has been slipping through the hedge — bring it in for the pot.* The
 * Heliwr's first hunt is close enough to the fold to practise stalking and one
 * exchange without sending a new character out toward a wolf pack or the boar.
 */
export const ERRAND_RABBIT = {
    id: 'rabbit-by-the-fold',
    giver: 'herder-anwen',
    prompt: 'Rabbits are at the hedge by the fold. Bring one in for the pot, and keep low as you go. If you empty the quiver before it is done, come back to me.',
    trigger: { kind: 'kill', species: 'rabbit' },
    continuesTo: 'rabbit-for-the-pot',
    offerWhen: after('bow-for-the-hedge'),
    resupply: {
        villager: 'herder-anwen',
        weaponId: 'bow',
        ammunition: 6,
        text: 'Anwen counts six more ash arrows into your hand. Bring the rabbit back before you empty these.',
    },
    ring: 1,
};
export const ERRAND_RABBIT_FOR_POT = {
    id: 'rabbit-for-the-pot',
    giver: 'herder-anwen',
    prompt: 'Dress the rabbit, then cook what you take at the watch fire.',
    objectives: [
        {
            id: 'butcher',
            text: 'Butcher the rabbit.',
            trigger: { kind: 'butcher', product: 'raw-meat' },
        },
        {
            id: 'cook',
            text: 'Cook the rabbit meat at the watch fire.',
            trigger: { kind: 'cook', product: 'cooked-meat' },
            implies: ['butcher'],
        },
    ],
    offerWhen: after(ERRAND_RABBIT.id),
    ring: 1,
};
export const ERRAND_FIND_MEDDYG = {
    // Kept under its old id so existing characters retain their progress.
    id: 'take-meat-to-the-meddyg',
    giver: 'herder-anwen',
    prompt: 'Keep the knife if you came without a blade; a cook should have one. Take some of the roasted nuts to Nest the Meddyg. She will show you what the watch carries for wounds.',
    facts: [{ kind: 'villager-location', villager: 'meddyg' }],
    trigger: { kind: 'talk-to', villager: 'meddyg' },
    offerWhen: after(ERRAND_ROAST_NUTS.id),
    ring: 1,
};
export const ERRAND_PREPARE_REMEDY = {
    id: 'remedy-for-the-watch',
    giver: 'meddyg',
    prompt: 'At the fire, crush the yarrow into the moss. A watch should carry its own remedy.',
    trigger: { kind: 'prepare', product: 'yarrow-poultice' },
    offerWhen: after(ERRAND_YARROW.id),
    ring: 1,
};
/** A reliable hunting tool, earned before the opening ever asks for a kill. */
export const ERRAND_BOW = {
    id: 'bow-for-the-hedge',
    giver: 'meddyg',
    prompt: 'You are fit to go farther now. Ask Deri for the yew bow before Anwen sends you to the hedge; a rabbit will not wait for bare hands.',
    facts: [{ kind: 'villager-location', villager: 'herder-deri' }],
    trigger: { kind: 'talk-to', villager: 'herder-deri' },
    offerWhen: after(ERRAND_PREPARE_REMEDY.id),
    reward: {
        itemId: 'bow',
        ammunition: 6,
        text: 'You have received a yew bow and six arrows from Deri. Take them to Anwen at the fold.',
    },
    ring: 1,
};
/**
 * The wolf hunt belongs after a newcomer has made one safe trip out and back.
 * Kept under its old id so saved characters carrying it can still finish it.
 */
export const ERRAND_WOLF = {
    id: 'wolf-at-the-pens',
    giver: 'herder-anwen',
    prompt: 'Something with teeth has been at the fold. Its tracks lead north-east beyond the quiet ground to the nearest wolf wood. Put it down and bring the pelt back—but keep clear of the wet oakwood, where the Chief of Boars wallows. If your quiver is empty, come to me before you go.',
    trigger: { kind: 'kill', species: 'wolf' },
    offerWhen: after('watch-fire-at-the-pen'),
    resupply: {
        villager: 'herder-anwen',
        weaponId: 'bow',
        ammunition: 6,
        text: 'Anwen counts six ash arrows into your hand. Bring the wolf down before it reaches the fold.',
    },
    ring: 2,
};
/**
 * *A ewe has strayed past the treeline, and the night is cold.* The Bugail's
 * first: navigation, forage, and a fire in the dark. Closed for now by getting a
 * fire going — the "held to dawn" tightening waits on firecraft state being
 * queryable (see `errands.ts`, the `fire-lit` trigger).
 */
export const ERRAND_EWE = {
    id: 'ewe-past-the-treeline',
    giver: 'herder-deri',
    prompt: 'One of the ewes is up the north-west slope past the trees from the fold, and the night is turning. You will want a fire.',
    trigger: { kind: 'fire-lit' },
    offerWhen: { kind: 'never' },
    ring: 2,
};
/**
 * *Stand the dusk watch at the pen.* A deterministic first lesson in defending
 * the herd: make the place costly for wolves before asking the player to fight
 * one. The later wolf errand is explicit about the animal to kill.
 */
export const ERRAND_WATCH = {
    id: 'watch-fire-at-the-pen',
    giver: 'herder-deri',
    prompt: 'Wolves are the danger here, not the cattle. Lay a watch fire inside or beside the pen; if a pack comes, the light may turn it before teeth reach the herd.',
    objectives: [
        {
            id: 'fire',
            text: 'Light a watch fire inside or beside the cattle pen.',
            trigger: { kind: 'fire-lit' },
        },
    ],
    offerWhen: {
        kind: 'all',
        rules: [after(ERRAND_RABBIT_FOR_POT.id), { kind: 'hour', from: 17, until: 23 }],
    },
    ring: 1,
};
/* -------------------------------------------------------------------------- *
 * The second ring — shared rungs
 * -------------------------------------------------------------------------- */
/**
 * *Clear the hazel deadfall.* The door to woodcraft (RC2-04). The axe is **not**
 * a reward the Elder hands over — it is a discoverable cache, an old tool left
 * lodged in the fallen hazel that the player finds and takes for themselves
 * (owner decision: a cache, no quest-giver gate, honouring M7-03's "no
 * curriculum"). So the errand carries no `reward`: the invitation points at the
 * deadfall, and the axe — and the craft — are what the wood gives up to whoever
 * goes and clears it. Closed by `chop`, which cannot happen until the axe is in
 * hand, so the doing is the only teacher exactly as RC2-02 asks.
 */
export const ERRAND_DEADFALL = {
    id: 'clear-the-hazel-deadfall',
    giver: 'elder',
    prompt: 'The hazels came down by the coppice and block the path. Clear the deadfall — and mind, ' +
        'there is a tool gone into it somewhere that the last hand never came back for.',
    trigger: { kind: 'chop' },
    offerWhen: after(ERRAND_RABBIT_FOR_POT.id),
    ring: 1,
};
/* -------------------------------------------------------------------------- *
 * The kit
 * -------------------------------------------------------------------------- */
export const ERRANDS = [
    ERRAND_FIND_ANWEN,
    ERRAND_SUPPER,
    ERRAND_WATCH_FIRE,
    ERRAND_ROAST_NUTS,
    ERRAND_FIND_MEDDYG,
    ERRAND_YARROW,
    ERRAND_PREPARE_REMEDY,
    ERRAND_BOW,
    ERRAND_RABBIT,
    ERRAND_RABBIT_FOR_POT,
    ERRAND_WATCH,
    ERRAND_WOLF,
    ERRAND_DEADFALL,
    // Kept resolvable for characters holding the old pre-arc errand.
    ERRAND_EWE,
];
const ERRAND_INDEX = new Map(ERRANDS.map((one) => [one.id, one]));
/** Every errand by id, for the sim's `closeErrands` to resolve a trigger. */
export const ERRAND_KIT = ERRAND_INDEX;
export function errand(id) {
    const found = ERRAND_INDEX.get(id);
    if (!found)
        throw new Error(`no errand '${id}'`);
    return found;
}
/**
 * Move characters carrying the original bare-handed rabbit gate onto the
 * revised safe-first route.
 *
 * The completed bow errand is the migration marker: a rabbit hunt reached
 * through the new progression always has it, while a pre-change active rabbit
 * never can. This makes the transform idempotent without adding a save-version
 * flag solely for one content correction.
 */
export function migrateOpeningErrands(state, tick) {
    let migrated = state;
    const legacyRabbit = migrated.active.find((one) => one.defId === ERRAND_RABBIT.id);
    const earnedBow = migrated.completed.some((one) => one.defId === ERRAND_BOW.id);
    if (legacyRabbit && !earnedBow) {
        const meddygStarted = migrated.active.some((one) => one.defId === ERRAND_FIND_MEDDYG.id) ||
            migrated.completed.some((one) => one.defId === ERRAND_FIND_MEDDYG.id);
        migrated = {
            ...migrated,
            active: migrated.active.flatMap((one) => {
                if (one.defId !== ERRAND_RABBIT.id)
                    return [one];
                return meddygStarted
                    ? []
                    : [{ defId: ERRAND_FIND_MEDDYG.id, acceptedTick: one.acceptedTick }];
            }),
        };
    }
    // The old handoff offered this errand only after the player returned to
    // Anwen, so a valid butcher/done event could precede acceptance. Cooking the
    // resulting meat proves both steps and safely repairs those persisted saves.
    const stuckPot = migrated.active.find((one) => one.defId === ERRAND_RABBIT_FOR_POT.id &&
        (one.progress?.cook ?? 0) >= 1 &&
        (one.progress?.butcher ?? 0) < 1);
    if (stuckPot) {
        migrated = {
            ...migrated,
            active: migrated.active.filter((one) => one !== stuckPot),
            completed: [
                ...migrated.completed,
                {
                    defId: stuckPot.defId,
                    acceptedTick: stuckPot.acceptedTick,
                    closedTick: tick,
                },
            ],
        };
    }
    // The retired watch waited on a random raid while the fire its prompt
    // encouraged actively reduced the chance of one. Move old completions to the
    // deterministic watch-fire id, and credit active testers who already stood
    // that broken watch rather than asking them to repeat the night.
    const legacyWatchId = 'stand-the-dusk-watch';
    const activeWatch = migrated.active.find((one) => one.defId === legacyWatchId);
    const completedWatch = migrated.completed.find((one) => one.defId === legacyWatchId);
    if (!activeWatch && !completedWatch)
        return migrated;
    const repairedWatch = completedWatch
        ? { ...completedWatch, defId: ERRAND_WATCH.id }
        : {
            defId: ERRAND_WATCH.id,
            acceptedTick: activeWatch.acceptedTick,
            closedTick: tick,
        };
    return {
        ...migrated,
        active: migrated.active.filter((one) => one.defId !== legacyWatchId),
        completed: [
            ...migrated.completed.filter((one) => one.defId !== legacyWatchId && one.defId !== ERRAND_WATCH.id),
            repairedWatch,
        ],
    };
}
/**
 * Repair characters who completed an opening equipment reward before rewards
 * were physical inventory items and loadouts were durable.
 *
 * Called only for the old `loadout: null` save shape, which is the migration
 * marker and prevents used equipment from being re-granted on later logins.
 */
export function repairOpeningRewards(state, current, tick, baseLoadout) {
    const cooked = state.completed.some((one) => one.defId === ERRAND_ROAST_NUTS.id);
    const earnedBow = state.completed.some((one) => one.defId === ERRAND_BOW.id);
    if (!cooked && !earnedBow)
        return null;
    const inventory = current ?? createInventory();
    if (cooked && countOf(inventory, 'knife') === 0)
        addItem(inventory, 'knife', 1, tick, false);
    if (earnedBow && countOf(inventory, 'bow') === 0)
        addItem(inventory, 'bow', 1, tick, false);
    return {
        inventory,
        loadout: {
            ...baseLoadout,
            weaponId: earnedBow ? 'bow' : 'knife',
            ...(earnedBow ? { ammunition: ERRAND_BOW.reward?.ammunition ?? 0 } : { edge: 'keen' }),
        },
    };
}
//# sourceMappingURL=errands.js.map